import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLeaf, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../config';

const GenusDetails = () => {
    const { genusId } = useParams();
    const [genus, setGenus] = useState(null);
    const [speciesList, setSpeciesList] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch Genus
                const genusResponse = await fetch(`${API_BASE_URL}/genera/read.php`);
                const genusData = await genusResponse.json();
                const foundGenus = genusData.records?.find(g => g.id == genusId);

                if (foundGenus) {
                    setGenus(foundGenus);

                    // Fetch Species
                    // We need to fetch all species and filter by genus_id. 
                    // Note: Ensure species API returns genus_id
                    const speciesResponse = await fetch(`${API_BASE_URL}/species/read.php`);
                    const speciesData = await speciesResponse.json();
                    if (speciesData.records) {
                        setSpeciesList(speciesData.records.filter(s => s.genus_id == genusId));
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [genusId]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }} className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full" />
            </div>
        );
    }

    if (!genus) {
        return <div className="p-20 text-center text-slate-500">Genus not found.</div>;
    }

    return (
        <section className="min-h-screen bg-white font-sans selection:bg-emerald-100 selection:text-emerald-900">
            <div className="max-w-7xl mx-auto px-6 pt-32 pb-24">
                {/* Breadcrumb / Back */}
                <Link to={`/flora-fauna/family/${genus.family_id}`} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400 hover:text-emerald-600 mb-8 transition-colors">
                    <FontAwesomeIcon icon={faArrowLeft} />
                    <span>Back to Family</span>
                </Link>

                {/* Header */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <span className="text-emerald-600 font-bold uppercase tracking-[0.2em] text-xs mb-4 block">
                            Taxonomic Genus
                        </span>
                        <div className="flex items-baseline gap-4 mb-4">
                            <h1 className="text-5xl md:text-7xl font-bold text-slate-900 font-display leading-tight italic">
                                {genus.name}
                            </h1>
                            <span className="text-slate-400 font-serif text-xl">{genus.common_name}</span>
                        </div>

                        <p className="text-lg text-slate-600 leading-relaxed max-w-xl border-l-4 border-emerald-500 pl-6">
                            {genus.description || "No description available for this genus."}
                        </p>
                    </motion.div>

                    <div className="relative aspect-video lg:aspect-square bg-slate-100 rounded-3xl overflow-hidden">
                        {genus.image_url ? (
                            <img src={genus.image_url} alt={genus.name} className="w-full h-full object-cover" />
                        ) : (
                            <div className="flex items-center justify-center h-full text-slate-300">
                                <FontAwesomeIcon icon={faLeaf} className="text-6xl" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Species List */}
                <div className="border-t border-slate-100 pt-16">
                    <h3 className="text-2xl font-bold text-slate-900 mb-10 font-display">Species Collection</h3>

                    {speciesList.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                            {speciesList.map((species, i) => (
                                <Link to={`/flora-fauna/species/${species.id}`} key={species.id} className="group block">
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.1 }}
                                        className="relative"
                                    >
                                        {/* Image Container */}
                                        <div className="aspect-[4/5] overflow-hidden bg-slate-100 mb-6 relative rounded-2xl">
                                            <div className="absolute inset-0 bg-emerald-900/0 group-hover:bg-emerald-900/10 transition-colors z-10 duration-500"></div>
                                            {species.images && species.images[0] ? (
                                                <img
                                                    src={species.images[0]}
                                                    alt={species.common_name}
                                                    className="w-full h-full object-cover transform scale-100 group-hover:scale-110 transition-transform duration-700 ease-out grayscale-[0.2] group-hover:grayscale-0"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-slate-300">
                                                    <FontAwesomeIcon icon={faLeaf} className="text-2xl" />
                                                </div>
                                            )}

                                            {(species.distribution?.native || []).includes("Sri Lanka") && (
                                                <div className="absolute top-4 left-4 z-20">
                                                    <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]"></div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Content */}
                                        <div className="mt-4 px-2">
                                            <h3 className="text-lg font-bold text-slate-900 mb-1 group-hover:text-emerald-800 transition-colors leading-tight font-display">
                                                {species.common_name}
                                            </h3>
                                            <p className="text-sm font-sans italic text-slate-400 group-hover:text-slate-500 transition-colors">
                                                {species.scientific_name}
                                            </p>
                                        </div>
                                    </motion.div>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="py-12 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                            <p className="text-slate-400 font-medium">No species recorded for this genus yet.</p>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default GenusDetails;
